/* GlobalProPaywall — mounts once at app root and listens for the global
   `orio:pro-required` event dispatched by the axios 402 interceptor.
   Dark Apple Fitness style — coherent with the rest of the app.
*/
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, Sparkles, X, Check } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

const BENEFITS = [
  "ORIO Walk illimitato + achievements",
  "Match, Park Room, chat",
  "SOS Emergenza · raggio 80 km",
  "Cassetto illimitato + PDF veterinario",
  "Cani illimitati · Radar completo",
];

const THROTTLE_KEY = "orio_paywall_last_shown_at";
const THROTTLE_MS = 6 * 60 * 60 * 1000;

export default function GlobalProPaywall() {
  const navigate = useNavigate();
  const { isPro, canStartTrial, startTrial } = useSubscription();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState("");

  useEffect(() => {
    const onEvt = (e) => {
      if (isPro) return;
      const raw = e?.detail || "";
      const force = typeof raw === "string" && raw.startsWith("force:");
      const msg = force ? raw.slice(6) : raw;
      if (!force) {
        try {
          const last = parseInt(localStorage.getItem(THROTTLE_KEY) || "0", 10);
          if (last && Date.now() - last < THROTTLE_MS) return;
        } catch { /* ignore */ }
      }
      setDetail(msg || "Funzione riservata agli abbonati ORIO Pro");
      setOpen(true);
      try { localStorage.setItem(THROTTLE_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("orio:pro-required", onEvt);
    return () => window.removeEventListener("orio:pro-required", onEvt);
  }, [isPro]);

  const close = () => setOpen(false);
  const go = () => {
    close();
    if (canStartTrial) startTrial();
    else navigate("/billing");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1900] flex items-end sm:items-center justify-center px-3 pb-[180px] sm:pb-3"
          data-testid="global-pro-paywall"
          onClick={close}
        >
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(14px)" }} />
          <motion.div
            initial={{ y: 80, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 80, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="relative w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] overflow-hidden border text-white"
            style={{
              background:
                "radial-gradient(120% 60% at 0% 0%, rgba(255,122,26,0.28) 0%, rgba(255,122,26,0) 60%), " +
                "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
              borderColor: "rgba(255,255,255,0.12)",
              boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 24px 48px -12px rgba(255,122,26,0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              aria-label="Chiudi"
              data-testid="global-paywall-close"
              className="absolute right-3 top-3 h-8 w-8 rounded-full flex items-center justify-center z-10 border"
              style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}
            >
              <X className="h-4 w-4 text-white/85" />
            </button>

            <div className="relative p-6 sm:p-7">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.20em] text-[#0F0F0F]"
                style={{ background: "linear-gradient(135deg, #FFB020 0%, #F4B83C 100%)" }}>
                <Crown className="h-3 w-3" /> ORIO Pro
              </div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-3 leading-[1.05] text-white">
                Funzione<br />
                <span className="orio-grad-text">riservata agli abbonati.</span>
              </h2>
              <p className="text-[13px] text-white/65 mt-2 leading-relaxed">
                {String(detail).replace(/\.+$/, "")}. Il piano gratuito ti permette di esplorare l&apos;app. Le funzioni operative sono su ORIO Pro.
              </p>

              <div className="mt-5 space-y-2">
                {BENEFITS.map((b) => (
                  <div key={b} className="flex items-start gap-2.5 text-[13px]">
                    <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-px"
                      style={{ background: "rgba(52,211,153,0.20)", border: "1px solid rgba(52,211,153,0.40)" }}>
                      <Check className="h-3 w-3 text-[#34D399]" />
                    </span>
                    <span className="text-white/85">{b}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border p-4"
                   style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.10)", backdropFilter: "blur(10px)" }}>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-3xl tabular-nums text-white">4,99&nbsp;€</span>
                  <span className="text-white/55 text-sm">/ mese</span>
                  <span className="ml-auto text-[11px] text-white/45">o €40/anno</span>
                </div>
                <div className="text-[11px] text-white/55 mt-0.5">
                  {canStartTrial ? "30 giorni gratis · disdici in un tap" : "Riattiva senza vincoli"}
                </div>
              </div>

              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={go}
                data-testid="global-paywall-cta"
                className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                  boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)",
                }}
              >
                <Sparkles className="h-4 w-4" />
                {canStartTrial ? "Inizia 30 giorni gratis" : "Vedi i piani"}
              </motion.button>
              <button
                type="button"
                onClick={close}
                data-testid="global-paywall-later"
                className="mt-2 w-full py-2 text-[12px] text-white/55 hover:text-white/85"
              >
                Continua a esplorare
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
